export const SITE_CACHE_TTL_MS = 24 * 60 * 60 * 1000;
export const SITE_BUILD_KEY = 'kayicom-site-build';
export const SITE_CLEARED_AT_KEY = 'kayicom-site-cleared-at';
export const SITE_RELOAD_KEY = 'kayicom-site-cache-reload';

export function getSiteBuildId() {
  return String(process.env.REACT_APP_BUILD_ID || 'dev').trim() || 'dev';
}

export function shouldClearSiteCache({ buildId, storedBuildId, clearedAt, now }) {
  const current = String(buildId || 'dev');
  if (String(storedBuildId || '') !== current) {
    return { clear: true, reason: 'deploy' };
  }
  const ts = Number(clearedAt);
  if (!Number.isFinite(ts) || ts <= 0 || now - ts >= SITE_CACHE_TTL_MS) {
    return { clear: true, reason: 'ttl' };
  }
  return { clear: false, reason: null };
}

export function cookieExpireAssignments(cookieHeader, { hostname = '', pathname = '/' } = {}) {
  const names = String(cookieHeader || '')
    .split(';')
    .map((part) => part.split('=')[0].trim())
    .filter(Boolean);
  const domains = [];
  if (hostname) {
    domains.push(hostname);
    domains.push(`.${hostname}`);
    const labels = hostname.split('.').filter(Boolean);
    if (labels.length >= 2) {
      domains.push(`.${labels.slice(-2).join('.')}`);
    }
  }
  const paths = ['/', pathname || '/'];
  const uniquePaths = [...new Set(paths.filter(Boolean))];
  const uniqueDomains = [...new Set(domains)];
  const lines = [];
  for (const name of names) {
    lines.push(`${name}=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/;max-age=0`);
    for (const path of uniquePaths) {
      lines.push(`${name}=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=${path};max-age=0`);
      for (const domain of uniqueDomains) {
        lines.push(
          `${name}=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=${path};domain=${domain};max-age=0`,
        );
      }
    }
  }
  return lines;
}

export function clearDocumentCookies(doc, loc) {
  const cookieSource = doc && typeof doc.cookie === 'string' ? doc.cookie : '';
  const hostname = loc?.hostname || '';
  const pathname = loc?.pathname || '/';
  const lines = cookieExpireAssignments(cookieSource, { hostname, pathname });
  if (doc) {
    lines.forEach((line) => {
      doc.cookie = line;
    });
  }
  return lines.length;
}

export async function clearCacheStorage(cacheApi) {
  if (!cacheApi?.keys) return 0;
  const keys = await cacheApi.keys();
  await Promise.all(keys.map((key) => cacheApi.delete(key)));
  return keys.length;
}

export function clearSessionCache(store) {
  if (!store) return;
  try {
    const reload = store.getItem(SITE_RELOAD_KEY);
    store.clear();
    if (reload) store.setItem(SITE_RELOAD_KEY, reload);
  } catch {
    /* ignore */
  }
}

export async function refreshSiteCache({
  now = Date.now(),
  buildId = getSiteBuildId(),
  storage,
  session,
  cookiesDoc,
  locationRef,
  cachesApi,
  reload,
} = {}) {
  const local = storage ?? (typeof localStorage !== 'undefined' ? localStorage : null);
  const sess = session ?? (typeof sessionStorage !== 'undefined' ? sessionStorage : null);
  const doc = cookiesDoc ?? (typeof document !== 'undefined' ? document : null);
  const loc = locationRef ?? (typeof window !== 'undefined' ? window.location : null);
  const cachesRef = cachesApi ?? (typeof caches !== 'undefined' ? caches : null);
  const doReload = reload ?? (() => {
    if (typeof window !== 'undefined') window.location.reload();
  });

  if (!local) return { cleared: false, reason: null, reloaded: false };

  let storedBuildId = '';
  let clearedAt = 0;
  try {
    storedBuildId = local.getItem(SITE_BUILD_KEY) || '';
    clearedAt = Number(local.getItem(SITE_CLEARED_AT_KEY) || 0);
  } catch {
    /* ignore */
  }

  const decision = shouldClearSiteCache({ buildId, storedBuildId, clearedAt, now });
  if (!decision.clear) return { cleared: false, reason: null, reloaded: false };

  clearDocumentCookies(doc, loc);
  await clearCacheStorage(cachesRef);
  clearSessionCache(sess);

  try {
    local.setItem(SITE_BUILD_KEY, String(buildId || 'dev'));
    local.setItem(SITE_CLEARED_AT_KEY, String(now));
  } catch {
    /* ignore */
  }

  const reloadToken = `${buildId}:${decision.reason}`;
  try {
    if (sess && sess.getItem(SITE_RELOAD_KEY) !== reloadToken) {
      sess.setItem(SITE_RELOAD_KEY, reloadToken);
      doReload();
      return { cleared: true, reason: decision.reason, reloaded: true };
    }
  } catch {
    /* ignore */
  }

  return { cleared: true, reason: decision.reason, reloaded: false };
}
